import { DoctorType, QueryFactory } from '@lmt/shared-types';
import { DoctorListDto } from '@lmt/shared-types';
import {
  sortingQueryParams,
  toScrollQueryParams,
} from '@lmt/unicorn-shared/app/components/common/table/operations';
import { TableTitle } from '@lmt/unicorn-shared/app/components/common/table/TableTitle';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { CheckBox } from '@lmt/unicorn-shared/app/components/common/ui-kit/CheckBox';
import { SearchInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/search/SearchInput';
import { useInsertKeyHandler } from '@lmt/unicorn-shared/app/components/dictionaries/useInsertKeyHandler';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import AddIcon from '@lmt/unicorn-shared/app/icons/plus-blue.svg?react';
import {
  useConvertedSorting,
  useScrollingState,
} from '@lmt/unicorn-shared/app/helpers/devextreme-helper';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameof } from '@lmt/unicorn-shared/app/helpers/nameof';
import { useQueryParamsInTab } from '@lmt/unicorn-shared/app/helpers/use-query-params-in-tab';
import React, { FC, useCallback } from 'react';
import {
  BooleanParam,
  StringParam,
  withDefault,
} from 'react-router-url-params';
import { DoctorsTable } from './components/DoctorsTable';

import styles from './DoctorsList.module.scss';
import { useDebouncedCallback } from 'use-debounce';
import { keepPreviousData } from '@tanstack/react-query';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';

interface DoctorListProps {
  doctorType: DoctorType;
}

export const DoctorsList: FC<DoctorListProps> = React.memo(
  function DoctorsList(props) {
    const [queryParams, setQueryParams] = useQueryParamsInTab({
      search: withDefault(StringParam, null),
      isOnlyActive: withDefault(BooleanParam, true),
      ...sortingQueryParams(nameof<DoctorListDto>('name'), false),
    });

    const i18next = useScopedTranslation('doctors.list');
    const forms = useGlobalForms();

    const { scrollParams, getRows, scrollToStart, pageSize } =
      useScrollingState();

    const openDoctorCreatingForm = useCallback(
      () => forms.openDoctorCreating(props.doctorType, true),
      [forms],
    );

    const { sorting, setSorting } = useConvertedSorting(
      queryParams,
      setQueryParams,
      scrollToStart,
    );

    const doctorsQuery = QueryFactory.DoctorQuery.useGetDoctorsQuery(
      {
        doctorType: props.doctorType,
        isOnlyActive: queryParams.isOnlyActive,
        searchQuery: queryParams.search,
        ...toScrollQueryParams({ ...queryParams, ...scrollParams }),
      },
      {
        notifyOnChangeProps: ['data'],
        placeholderData: keepPreviousData,
      },
    );

    const onSearchUpdated = useDebouncedCallback((value: string) => {
      scrollToStart();
      setQueryParams({
        search: value,
      });
    }, 300);

    useInsertKeyHandler(openDoctorCreatingForm);

    return (
      <div
        className={styles.tableContainer}
        data-test-id="table-page-container"
      >
        <div className={styles.tableToolbar}>
          <TableTitle
            title={
              props.doctorType === DoctorType.Clinic
                ? i18next.t('attending_doctors_title')
                : i18next.t('external_doctors_title')
            }
            count={doctorsQuery.data?.totalCount ?? 0}
          />
          <div className={styles.searchField}>
            <SearchInput
              placeholder={i18next.t('search_placeholder')}
              bordered={true}
              onChange={(e) => onSearchUpdated(e.target.value)}
              onSearchIconClick={onSearchUpdated}
              onClear={onSearchUpdated}
              initialValue={queryParams.search}
              allowAnySearch
              blurOnEnter={false}
            />
          </div>
          <div className={styles.filters}>
            <CheckBox
              defaultChecked={queryParams.isOnlyActive}
              onClick={(event) => {
                scrollToStart();
                setQueryParams({
                  isOnlyActive: event.currentTarget.checked,
                });
              }}
              title={i18next.t('table_filter_active')}
              name={`doctorSearchIsActive_${props.doctorType}`}
            />
            <Button
              className={styles.addButton}
              color={ButtonColor.Primary}
              iconComponent={<AddIcon />}
              onClick={openDoctorCreatingForm}
              title={
                props.doctorType === DoctorType.Clinic
                  ? i18next.t('add_attending_doctor_button')
                  : i18next.t('add_family_doctor_button')
              }
              testId={'plus-button'}
            />
          </div>
        </div>

        <div className={styles.doctorsTable}>
          <DoctorsTable
            data={doctorsQuery.data?.data ?? []}
            isNoDoctorsFound={doctorsQuery.data?.totalCount === 0}
            getRows={getRows}
            isLoading={doctorsQuery.isFetching}
            skip={scrollParams.skip}
            pageSize={pageSize}
            totalRowCount={doctorsQuery.data?.totalCount ?? pageSize}
            sorting={sorting}
            setSorting={setSorting}
            doctorType={props.doctorType}
          />
        </div>
      </div>
    );
  },
);
